import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type { ApiPosition } from "@tradingdroid/frontends-types";

export const CLOSED_POSITIONS_FETCH_CAP = 1000;

const QUERY_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const toQueryDatetime = (value: string): string => {
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.format(QUERY_DATETIME_FORMAT) : value;
};

const buildPositionsQuery = (
  from: string,
  to: string,
  executionId: number | null,
  scopeExecutionIds: number[],
): string => {
  const conditions: string[] = ["status_name.EQ:'closed'"];
  conditions.push(`end_ts.GE:'${toQueryDatetime(from)}'`);
  conditions.push(`end_ts.LE:'${toQueryDatetime(to)}'`);
  if (executionId !== null) {
    conditions.push(`execution_id.EQ:${executionId}`);
  } else if (scopeExecutionIds.length > 0) {
    conditions.push(`execution_id.IN:'${scopeExecutionIds.join(",")}'`);
  }
  return conditions.join(" AND ");
};

/**
 * Positions closed between `from` and `to` (UTC) for one execution, or for every execution in scope.
 */
export const useClosedPositions = (
  from: string,
  to: string,
  executionId: number | null,
  scopeExecutionIds: number[],
) => {
  const { showToast } = useToastActions();
  const [positions, setPositions] = useState<ApiPosition[]>([]);
  const [loading, setLoading] = useState(false);
  const [hitCap, setHitCap] = useState(false);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // A stable string of the IDs avoids refetching when the array reference changes but not its contents.
  const scopeKey = scopeExecutionIds.join(",");
  const queryKey = `${from}|${to}|${executionId}|${scopeKey}`;

  useEffect(() => {
    let cancelled = false;

    const fetchPositions = async () => {
      setLoading(true);
      setHitCap(false);

      // No executions in scope: render empty without hitting the API
      // (a wide-open IN clause would pull non-portfolio rows).
      if (scopeExecutionIds.length === 0) {
        setPositions([]);
        setLoadedKey(queryKey);
        setLoading(false);
        return;
      }

      try {
        const response = await tdApi.getPositions({
          q: buildPositionsQuery(from, to, executionId, scopeExecutionIds),
          limit: CLOSED_POSITIONS_FETCH_CAP,
          sort_by: "end_ts:DESC",
        });

        if (cancelled) return;

        const items = response?.items ?? [];
        setPositions(items);
        setLoadedKey(queryKey);
        setHitCap(items.length >= CLOSED_POSITIONS_FETCH_CAP);
      } catch (error) {
        if (cancelled) return;
        console.error("[useClosedPositions] Failed to load positions:", error);
        // Drop stale rows so the page doesn't show old results under new filters.
        setPositions([]);
        setLoadedKey(queryKey);
        setHitCap(false);
        showToast({ message: "Failed to load closed positions", type: "danger" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPositions();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, executionId, scopeKey, showToast]);

  // True until the positions match the current filters, so callers never pair old rows with new filters.
  const stale = loadedKey !== queryKey;

  return { positions, loading, hitCap, stale };
};
